import { memo } from 'react';
import type { Bounds, Connection, ConnectionId, ObjectId } from '@/types';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { useSelectionStore } from '@/store/selectionStore';
import { useEngine } from '@/app/EngineContext';
import { connectionSegment, midpoint } from '@/core/graph/connectionGeometry';
import { isSymmetric } from '@/core/graph/connectionGraph';
import styles from './ConnectionLayer.module.css';

/**
 * Draws persisted connections as SVG segments trimmed to the objects' edges, with
 * arrowheads for directed types and dashes for weaker relationships. Each edge
 * subscribes to the world bounds of its two (visible-anchor) endpoints only, so
 * moving one object redraws its edges and nothing else. Rendered under objects in
 * the world layer; hit testing is done by the interaction adapter, not here.
 */
export const ConnectionLayer = () => {
  const connections = useWorkspaceStore((s) => s.workspaces[s.activeWorkspaceId]?.connections);
  if (!connections) return null;
  const ids = Object.keys(connections) as ConnectionId[];
  if (ids.length === 0) return null;
  return (
    <svg className={styles.layer} data-testid="connection-layer" aria-hidden="true">
      <defs>
        <marker id="con-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" />
        </marker>
        <marker
          id="con-arrow-selected"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="8"
          markerHeight="8"
          orient="auto"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" className={styles.selectedMarker} />
        </marker>
        <marker id="con-block" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="8" markerHeight="8" orient="auto">
          <path d="M 2 0 L 2 10 M 5 0 L 5 10" className={styles.blockMarker} />
        </marker>
      </defs>
      {ids.map((id) => {
        const c = connections[id];
        return c ? <ConnectionView key={id} connection={c} /> : null;
      })}
    </svg>
  );
};

/**
 * World AABB of an object's visible anchor. The scene graph already holds world
 * bounds (it syncs before React subscribers run), so this is two map lookups per
 * store change instead of a matrix walk; the string key keeps the selector stable.
 */
export const useAnchorBounds = (id: ObjectId): Bounds | null => {
  const { scene, ensureSynced } = useEngine();
  const key = useWorkspaceStore(() => {
    ensureSynced();
    const anchor = scene.visibleAnchorOf(id);
    const obj = scene.get(anchor);
    const b = obj?.visible ? scene.boundsOf(anchor) : undefined;
    return b ? `${b.minX},${b.minY},${b.maxX},${b.maxY}` : null;
  });
  if (key === null) return null;
  const [minX = 0, minY = 0, maxX = 0, maxY = 0] = key.split(',').map(Number);
  return { minX, minY, maxX, maxY };
};

const DASHED: ReadonlySet<Connection['type']> = new Set(['references', 'related-to']);

const ConnectionView = memo(({ connection }: { connection: Connection }) => {
  const source = useAnchorBounds(connection.sourceId);
  const target = useAnchorBounds(connection.targetId);
  const selected = useSelectionStore((s) => s.selection.connectionIds.includes(connection.id));
  if (!source || !target) return null;
  if (
    source.minX === target.minX &&
    source.minY === target.minY &&
    source.maxX === target.maxX &&
    source.maxY === target.maxY
  )
    return null;
  const { a, b } = connectionSegment(source, target);
  const mid = midpoint(a, b);
  const directed = !isSymmetric(connection.type);
  const markerEnd =
    connection.type === 'blocks'
      ? 'url(#con-block)'
      : directed
        ? selected
          ? 'url(#con-arrow-selected)'
          : 'url(#con-arrow)'
        : undefined;
  return (
    <g
      data-connection-id={connection.id}
      data-source={connection.sourceId}
      data-target={connection.targetId}
      data-type={connection.type}
      data-selected={selected || undefined}
      data-testid="connection"
    >
      <line
        x1={a.x}
        y1={a.y}
        x2={b.x}
        y2={b.y}
        className={styles.line}
        markerEnd={markerEnd}
        strokeDasharray={DASHED.has(connection.type) ? '6 4' : undefined}
      />
      {(connection.label || selected) && (
        <text x={mid.x} y={mid.y - 6} className={styles.label} textAnchor="middle">
          {connection.label || connection.type}
        </text>
      )}
    </g>
  );
});
ConnectionView.displayName = 'ConnectionView';
